'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { projectPhoto } from '@/lib/projectPhoto';
import { PhotoPrompt } from '@/components/ui/NextVisual';
import SitePhoto from '@/components/ui/SitePhoto';
import { SITE_PHOTOS } from '@/data/sitePhotos';
import ScrollReveal from '@/app/components/ScrollReveal';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import CTAPrimary from '@/components/ui/CTAPrimary';
import CTASecondary from '@/components/ui/CTASecondary';
import { COMPANY } from '@/data/company';

// A designer-led home we manufactured and built (Gubbi Gudu — Navyashree & Sandhya Jamadagni)
const DESIGNER_PHOTOS = [
  projectPhoto('gubbi-gudu-villa-anjanapura', 'living-room-walnut-tv-unit-and-fluted-panel-wide-view.jpg'),
  projectPhoto('gubbi-gudu-villa-anjanapura', 'white-kitchen-tall-units-with-built-in-appliances.jpg'),
  projectPhoto('gubbi-gudu-villa-anjanapura', 'kids-room-with-loft-bed-and-wall-shelves.jpg'),
];

// ─── DATA ─────────────────────────────────────────────────────────────────────

const WHAT_WE_DO = [
  {
    label: 'Manufacturing',
    detail: 'Modular components built to your specifications — kitchens, wardrobes, TV units, vanities and more.',
    index: '01',
  },
  {
    label: 'Assembly',
    detail: 'Standardised assembly processes that ensure components fit and function as designed.',
    index: '02',
  },
  {
    label: 'Quality Control',
    detail: 'Verification at the facility before anything reaches site. Finish, fit and function checked.',
    index: '03',
  },
  {
    label: 'Installation',
    detail: 'Supervised on-site installation. Your design intent, executed on the ground.',
    index: '04',
  },
];

const PRODUCTS = [
  'Kitchens',
  'Wardrobes',
  'TV Units',
  'Crockery Units',
  'Beds',
  'Vanities',
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────

// `journey`: the next-visual cards for this page, built on the server (see src/data/journeyItems.ts)
export default function InteriorDesignersContent({ journey }: { journey?: React.ReactNode } = {}) {
  const [hoveredService, setHoveredService] = useState<number | null>(null);

  return (
    <div className="bg-warm-white min-h-screen" style={{ fontFamily: 'var(--font-sans)' }}>

      {/* ── BREADCRUMBS ─────────────────────────────────────────── */}
      <div className="container-willow pt-8 pb-0">
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Professional Partnerships', href: '/b2b' },
            { label: 'Interior Designers' },
          ]}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════
          SECTION 1 — HERO
          Asymmetric: large typographic statement left, tall image right
          ══════════════════════════════════════════════════════════ */}
      <section aria-labelledby="id-h1" className="overflow-hidden">
        <div className="container-willow">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] min-h-[92vh] items-stretch gap-0">

            {/* Left column — editorial text block */}
            <div className="flex flex-col justify-between py-16 lg:py-24 lg:pr-20">

              {/* Top: label */}
              <div className="flex items-center gap-3">
                <span className="accent-line" aria-hidden="true" />
                <span className="text-section-label">FOR INTERIOR DESIGNERS</span>
              </div>

              {/* Middle: headline */}
              <div className="flex flex-col gap-8 my-auto py-12">
                {/* Large editorial headline — stacked, not centered */}
                <div>
                  <p
                    style={{
                      fontSize: 'clamp(0.65rem, 1.1vw, 0.8rem)',
                      fontWeight: 700,
                      letterSpacing: '0.25em',
                      color: 'var(--primary)',
                      textTransform: 'uppercase',
                      marginBottom: '1.25rem',
                    }}
                  >
                    The question designers ask
                  </p>
                  <blockquote
                    style={{
                      fontSize: 'clamp(1.1rem, 2.2vw, 1.5rem)',
                      fontWeight: 400,
                      lineHeight: 1.55,
                      color: 'var(--secondary)',
                      borderLeft: '2px solid var(--primary)',
                      paddingLeft: '1.25rem',
                      fontStyle: 'italic',
                      maxWidth: '32rem',
                    }}
                  >
                    "I have interior projects but need a reliable manufacturing and execution partner. Who can I work with?"
                  </blockquote>
                </div>

                <div>
                  <h1
                    id="id-h1"
                    style={{
                      fontSize: 'clamp(3.5rem, 9vw, 7.5rem)',
                      fontWeight: 800,
                      lineHeight: 0.92,
                      letterSpacing: '-0.05em',
                      color: 'var(--foreground)',
                    }}
                  >
                    {/* Topic line inside the H1 so the heading says what the page is about; slogan below */}
                    <span className="block text-section-label mb-4 leading-normal">Manufacturing Partner for Interior Designers</span>
                    YOU<br />
                    <span style={{ color: 'var(--primary)', WebkitTextStroke: '0px' }}>DESIGN.</span>
                  </h1>
                  <h2
                    style={{
                      fontSize: 'clamp(3.5rem, 9vw, 7.5rem)',
                      fontWeight: 800,
                      lineHeight: 0.92,
                      letterSpacing: '-0.05em',
                      color: 'var(--foreground)',
                      marginTop: '0.05em',
                    }}
                  >
                    WE<br />BUILD.
                  </h2>
                </div>

                <p
                  style={{
                    fontSize: 'clamp(1rem, 1.6vw, 1.15rem)',
                    lineHeight: 1.65,
                    color: 'var(--secondary)',
                    maxWidth: '30rem',
                  }}
                >
                  Willow provides manufacturing and execution support behind interior designers' projects. Bring your design intent — we handle production, assembly, quality control and installation.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <CTAPrimary label="Discuss a Project" href="/contact" />
                  <CTASecondary label="View Our Work" href="/projects" />
                </div>
              </div>

              {/* Bottom: who this is for */}
              <div
                className="flex flex-wrap gap-x-6 gap-y-2 pt-6"
                style={{ borderTop: '1px solid var(--border)' }}
              >
                {[
                  'Freelance Designers',
                  'Small Studios',
                  'Medium Studios',
                  'Architects with Interior Projects',
                  'Design Teams',
                ]?.map((audience) => (
                  <span
                    key={audience}
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'var(--secondary)',
                      opacity: 0.65,
                    }}
                  >
                    {audience}
                  </span>
                ))}
              </div>
            </div>

            {/* Right column — tall image, flush to edge */}
            <div className="relative hidden lg:flex flex-col" style={{ minHeight: '600px' }}>
              {/* Main image — fills height */}
              <div className="flex-1 relative">
                <SitePhoto photo={SITE_PHOTOS.studioLounge} companions={[SITE_PHOTOS.drawerHardware]} aspectRatio="3/7" fillParent className="w-full h-full absolute inset-0" priority />
              </div>
              {/* Small floating label — bottom right */}
              <div
                className="absolute bottom-8 right-8 px-4 py-2"
                style={{
                  backgroundColor: 'rgba(23,22,20,0.78)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <span
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    color: 'var(--primary)',
                  }}
                >
                  WILLOW · MANUFACTURING &amp; EXECUTION
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 2 — WHY WILLOW
          Dark panel — editorial, concise
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="why-willow-heading"
        style={{ backgroundColor: 'var(--deep)', color: '#FAF8F5' }}
      >
        <ScrollReveal>
          <div className="container-willow py-20 md:py-28">
            <div className="grid lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-24 items-center">

              {/* Left: label + heading */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="accent-line"
                    aria-hidden="true"
                    style={{ backgroundColor: '#AA7D53' }}
                  />
                  <span
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    WHY WILLOW
                  </span>
                </div>
                <h2
                  id="why-willow-heading"
                  style={{
                    fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                    fontWeight: 700,
                    lineHeight: 1.05,
                    letterSpacing: '-0.04em',
                    color: '#FAF8F5',
                  }}
                >
                  Willow began<br />with manufacturing.
                </h2>
              </div>

              {/* Right: copy */}
              <div className="flex flex-col gap-6">
                <p
                  style={{
                    fontSize: 'clamp(1rem, 1.6vw, 1.2rem)',
                    lineHeight: 1.7,
                    color: 'rgba(250,248,245,0.75)',
                  }}
                >
                  Our manufacturing experience became one of the foundations of how we approach interior execution. We understand what it takes to move from a design drawing to a finished, installed interior — because we've built the production infrastructure to do it.
                </p>
                <p
                  style={{
                    fontSize: 'clamp(0.9rem, 1.4vw, 1.05rem)',
                    lineHeight: 1.7,
                    color: 'rgba(250,248,245,0.55)',
                  }}
                >
                  For a designer, that means a partner who understands your intent and has the capability to execute it — without you having to manage every production detail yourself.
                </p>

                {/* Chain */}
                <div
                  className="flex items-center gap-2 flex-wrap pt-4"
                  style={{ borderTop: '1px solid rgba(250,248,245,0.1)' }}
                >
                  {['DESIGN', 'MANUFACTURING', 'QUALITY', 'INSTALLATION']?.map((step, i, arr) => (
                    <React.Fragment key={step}>
                      <span
                        style={{
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                          color: step === 'MANUFACTURING' ? 'var(--primary)' : 'rgba(250,248,245,0.5)',
                        }}
                      >
                        {step}
                      </span>
                      {i < arr?.length - 1 && (
                        <span
                          style={{ color: 'var(--primary)', opacity: 0.4, fontSize: '0.75rem' }}
                          aria-hidden="true"
                        >
                          →
                        </span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 3 — WHAT WE DO
          Unconventional: large numbered list with hover reveal
          ══════════════════════════════════════════════════════════ */}
      <section aria-labelledby="what-we-do-heading" className="border-b border-border">
        <div className="container-willow py-20 md:py-28">

          <ScrollReveal>
            <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-20 items-end mb-16">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="accent-line" aria-hidden="true" />
                  <span className="text-section-label">WHAT WE DO</span>
                </div>
                <h2
                  id="what-we-do-heading"
                  style={{
                    fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: '-0.03em',
                    color: 'var(--foreground)',
                  }}
                >
                  The four things<br />we handle.
                </h2>
              </div>
              <p
                style={{
                  fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
                  lineHeight: 1.7,
                  color: 'var(--secondary)',
                  maxWidth: '36rem',
                }}
              >
                From the first component off the machine to the final installation on site — Willow manages the production and execution side so you can stay focused on the design.
              </p>
            </div>
          </ScrollReveal>

          {/* Service rows — large, editorial, hover-interactive */}
          <div style={{ borderTop: '1px solid var(--border)' }}>
            {WHAT_WE_DO?.map((service, idx) => (
              <ScrollReveal key={service?.index}>
                <div
                  className="group cursor-default"
                  onMouseEnter={() => setHoveredService(idx)}
                  onMouseLeave={() => setHoveredService(null)}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    padding: '1.75rem 0',
                    transition: 'background-color 0.2s ease',
                    backgroundColor:
                      hoveredService === idx ? 'var(--stone)' : 'transparent',
                  }}
                >
                  <div className="grid lg:grid-cols-[3rem_1fr_1.4fr_2rem] gap-6 items-center px-2">
                    {/* Number */}
                    <span
                      style={{
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.15em',
                        color: 'var(--primary)',
                        opacity: 0.6,
                      }}
                    >
                      {service?.index}
                    </span>

                    {/* Label */}
                    <h3
                      style={{
                        fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)',
                        fontWeight: 700,
                        letterSpacing: '-0.03em',
                        lineHeight: 1.1,
                        color: 'var(--foreground)',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {service?.label}
                    </h3>

                    {/* Detail — visible on hover desktop, always visible mobile */}
                    <p
                      style={{
                        fontSize: '0.9rem',
                        lineHeight: 1.65,
                        color: 'var(--secondary)',
                        opacity: hoveredService === idx ? 1 : 0,
                        transform: hoveredService === idx ? 'translateY(0)' : 'translateY(4px)',
                        transition: 'opacity 0.25s ease, transform 0.25s ease',
                      }}
                      className="hidden lg:block"
                    >
                      {service?.detail}
                    </p>

                    {/* Arrow */}
                    <span
                      style={{
                        color: 'var(--primary)',
                        fontSize: '1.1rem',
                        opacity: hoveredService === idx ? 1 : 0.25,
                        transition: 'opacity 0.2s ease, transform 0.2s ease',
                        transform: hoveredService === idx ? 'translateX(4px)' : 'translateX(0)',
                        textAlign: 'right',
                      }}
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </div>

                  {/* Mobile detail — always visible */}
                  <p
                    className="lg:hidden px-2 mt-2"
                    style={{
                      fontSize: '0.875rem',
                      lineHeight: 1.65,
                      color: 'var(--secondary)',
                      paddingLeft: 'calc(3rem + 1.5rem)',
                    }}
                  >
                    {service?.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* Products we manufacture — compact tag list */}
          <ScrollReveal>
            <div className="mt-14 pt-10" style={{ borderTop: '1px solid var(--border)' }}>
              <p
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--secondary)',
                  opacity: 0.55,
                  marginBottom: '1rem',
                }}
              >
                Modular products we manufacture
              </p>
              <div className="flex flex-wrap gap-3">
                {PRODUCTS?.map((product) => (
                  <span
                    key={product}
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      color: 'var(--foreground)',
                      border: '1px solid var(--border)',
                      padding: '0.4rem 0.9rem',
                    }}
                  >
                    {product}
                  </span>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 4 — HOW IT WORKS
          Asymmetric: large image left, editorial steps right
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="how-it-works-heading"
        style={{ backgroundColor: 'var(--stone)' }}
      >
        <div className="container-willow py-20 md:py-28">
          <ScrollReveal>
            <div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-center">

              {/* Left: image */}
              <div className="relative">
                <SitePhoto photo={SITE_PHOTOS.team} aspectRatio="4/5" className="w-full" />
                {/* Offset label block */}
                <div
                  className="absolute -bottom-6 -right-6 hidden lg:flex flex-col gap-1 px-5 py-4"
                  style={{ backgroundColor: 'var(--deep)', maxWidth: '200px' }}
                >
                  <span
                    style={{
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    THE PROCESS
                  </span>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      color: 'rgba(250,248,245,0.6)',
                      lineHeight: 1.5,
                    }}
                  >
                    Design → Manufacture → Install
                  </span>
                </div>
              </div>

              {/* Right: steps */}
              <div className="flex flex-col gap-8">
                <div>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="accent-line" aria-hidden="true" />
                    <span className="text-section-label">HOW IT WORKS</span>
                  </div>
                  <h2
                    id="how-it-works-heading"
                    style={{
                      fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                      fontWeight: 700,
                      lineHeight: 1.1,
                      letterSpacing: '-0.03em',
                      color: 'var(--foreground)',
                    }}
                  >
                    Three steps.<br />One partner.
                  </h2>
                </div>

                {/* Steps — editorial, not numbered circles */}
                <div className="flex flex-col gap-0" style={{ borderTop: '1px solid var(--border)' }}>
                  {[
                    {
                      step: 'You Design',
                      note: 'Bring your drawings, specifications and design intent. You remain the design authority throughout.',
                    },
                    {
                      step: 'We Manufacture',
                      note: 'Willow produces the modular components at our facility — with quality checks before anything leaves.',
                    },
                    {
                      step: 'We Install',
                      note: 'Supervised installation on site. Your design, executed as intended.',
                    },
                  ]?.map((item, i) => (
                    <div
                      key={item?.step}
                      className="flex gap-6 items-start py-7"
                      style={{ borderBottom: '1px solid var(--border)' }}
                    >
                      {/* Step indicator */}
                      <div
                        style={{
                          width: '2rem',
                          height: '2rem',
                          border: '1px solid var(--primary)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: '0.15rem',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            color: 'var(--primary)',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {String(i + 1)?.padStart(2, '0')}
                        </span>
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <h3
                          style={{
                            fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            color: 'var(--foreground)',
                          }}
                        >
                          {item?.step}
                        </h3>
                        <p
                          style={{
                            fontSize: '0.9rem',
                            lineHeight: 1.65,
                            color: 'var(--secondary)',
                          }}
                        >
                          {item?.note}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          SECTION 5 — PROJECTS / PROOF
          Full-bleed image grid — let the work speak
          ══════════════════════════════════════════════════════════ */}
      <section aria-labelledby="proof-heading" className="border-t border-border">
        <div className="container-willow py-20 md:py-28">
          <ScrollReveal>
            <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="accent-line" aria-hidden="true" />
                  <span className="text-section-label">THE WORK</span>
                </div>
                <h2
                  id="proof-heading"
                  style={{
                    fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: '-0.03em',
                    color: 'var(--foreground)',
                  }}
                >
                  Projects speak<br />louder than claims.
                </h2>
              </div>
              <Link
                href="/projects"
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'gap 0.2s ease',
                }}
                className="group"
                aria-label="View all Willow projects"
              >
                View All Projects
                <span
                  style={{ transition: 'transform 0.2s ease' }}
                  className="group-hover:translate-x-1"
                  aria-hidden="true"
                >
                  →
                </span>
              </Link>
            </div>
          </ScrollReveal>

          {/* Asymmetric image grid — 3 images, varied sizes */}
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {/* Large left image */}
            <ScrollReveal className="col-span-12 md:col-span-7">
<div>
                <SitePhoto photo={DESIGNER_PHOTOS[0]} aspectRatio="4/3" sizes="(min-width: 768px) 40vw, 100vw" />
                <PhotoPrompt href={DESIGNER_PHOTOS[0].projectHref} className="mt-3">
                  {DESIGNER_PHOTOS[0].projectName}, designed by Navyashree &amp; Sandhya Jamadagni — see the full project
                </PhotoPrompt>
              </div>
            </ScrollReveal>

            {/* Right column — two stacked images */}
            <div className="col-span-12 md:col-span-5 flex flex-col gap-4 md:gap-6">
              <ScrollReveal>
                <SitePhoto photo={DESIGNER_PHOTOS[1]} aspectRatio="4/3" sizes="(min-width: 768px) 30vw, 100vw" />
              </ScrollReveal>
              <ScrollReveal>
                <SitePhoto photo={DESIGNER_PHOTOS[2]} aspectRatio="4/3" sizes="(min-width: 768px) 30vw, 100vw" />
              </ScrollReveal>
            </div>
          </div>

          <ScrollReveal>
            <p
              className="mt-8 text-center"
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--secondary)',
                opacity: 0.45,
              }}
            >
              Real Willow project imagery will replace these placeholders
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ── NEXT VISUALS — where to look next (visual journey) ── */}
      {journey}

      {/* ══════════════════════════════════════════════════════════
          SECTION 6 — CTA
          Dark, confident, minimal
          ══════════════════════════════════════════════════════════ */}
      <section
        aria-labelledby="cta-heading"
        style={{ backgroundColor: 'var(--deep)' }}
      >
        <ScrollReveal>
          <div className="container-willow py-24 md:py-32">
            <div className="grid lg:grid-cols-[1fr_auto] gap-12 items-center">

              {/* Left: copy */}
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-3">
                  <span
                    className="accent-line"
                    aria-hidden="true"
                    style={{ backgroundColor: '#AA7D53' }}
                  />
                  <span
                    style={{
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      color: 'var(--primary)',
                    }}
                  >
                    HAVE A PROJECT?
                  </span>
                </div>

                <h2
                  id="cta-heading"
                  style={{
                    fontSize: 'clamp(2.5rem, 6vw, 5rem)',
                    fontWeight: 800,
                    lineHeight: 0.95,
                    letterSpacing: '-0.05em',
                    color: '#FAF8F5',
                  }}
                >
                  Let&apos;s talk.
                </h2>

                <p
                  style={{
                    fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
                    lineHeight: 1.7,
                    color: 'rgba(250,248,245,0.6)',
                    maxWidth: '32rem',
                  }}
                >
                  Tell us about your project. We'll start by understanding what you're working on and whether Willow is the right manufacturing and execution partner for it.
                </p>
              </div>

              {/* Right: CTAs */}
              <div className="flex flex-col gap-4 lg:items-end">
                <CTAPrimary label="Start the Conversation" href="/contact" />
                <a
                  href={COMPANY?.whatsapp?.message}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold tracking-wide transition-colors duration-150"
                  style={{
                    border: '1px solid rgba(250,248,245,0.2)',
                    color: 'rgba(250,248,245,0.75)',
                  }}
                  aria-label="Contact Willow on WhatsApp"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>

    </div>
  );
}
